import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { SEO } from "@/components/seo/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Mic,
  Type,
  FileSpreadsheet,
  Image as ImageIcon,
  Sparkles,
  Loader2,
  Download,
  ExternalLink,
  Database,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { VoiceCapture } from "@/components/smart-entry/VoiceCapture";
import { SheetCapture } from "@/components/smart-entry/SheetCapture";
import { ImageCapture, type CapturedImage } from "@/components/smart-entry/ImageCapture";
import { StructuredPreviewTable } from "@/components/smart-entry/StructuredPreviewTable";
import { downloadXlsx, type ParsedFile } from "@/lib/file-parsing";

const STRUCTURING_PROMPT =
  "You are a business data structuring assistant that works across ANY industry. You will receive unstructured business information from text, voice transcript, a spreadsheet, or an image such as a receipt or screenshot. Detect the industry context from what is described, then convert it into clean structured rows with the most appropriate columns for that specific business type. Common fields may include Date, Product or Service, Quantity, Unit Price, Total Revenue, Returns or Issues, Notes, Customer Location, but adapt and rename fields intelligently based on the actual industry and input described. Return the result as a JSON array of objects.";

const GOOGLE_SCOPES = [
  "https://www.googleapis.com/auth/spreadsheets",
  "https://www.googleapis.com/auth/drive.file",
].join(" ");

type Mode = "voice" | "text" | "sheet" | "image";

/** Turn a backend "non-2xx" error into the real, readable reason. */
async function readFunctionError(err: any): Promise<{ message: string; status?: number }> {
  const status: number | undefined = err?.context?.status;
  let detail = "";
  try {
    const body = await err?.context?.json?.();
    detail = body?.error || body?.message || "";
  } catch { /* ignore */ }
  if (status === 429) return { status, message: "The AI is busy right now. Please try again in a minute." };
  if (status === 402) return { status, message: "AI credits have run out for this workspace." };
  if (status === 401) return { status, message: "Your session expired. Please sign in again." };
  if (status === 413) return { status, message: "That input is too large. Try a smaller image or fewer rows." };
  return { status, message: detail || err?.message || "Something went wrong. Please try again." };
}

const SmartDataEntry = () => {
  const [mode, setMode] = useState<Mode>("voice");
  const [transcript, setTranscript] = useState("");
  const [typedText, setTypedText] = useState("");
  const [sheet, setSheet] = useState<ParsedFile | null>(null);
  const [image, setImage] = useState<CapturedImage | null>(null);

  const [analysing, setAnalysing] = useState(false);
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const columns = useMemo(() => {
    const seen: string[] = [];
    rows.forEach((r) => Object.keys(r).forEach((k) => { if (!seen.includes(k)) seen.push(k); }));
    return seen;
  }, [rows]);

  // Complete Google OAuth when returning from consent screen.
  useEffect(() => {
    const code = searchParams.get("code");
    if (!code || sessionStorage.getItem("sde_google_oauth") !== "1") return;
    sessionStorage.removeItem("sde_google_oauth");
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const { data, error: fnError } = await supabase.functions.invoke("google-sheets-auth", {
          body: {
            action: "exchange_code",
            code,
            redirectUri: `${window.location.origin}/smart-data-entry`,
          },
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        if (fnError || data?.error) throw new Error(data?.error || fnError?.message);
        toast.success("Google account connected. Click 'Export to Google Sheets' again.");
      } catch (e: any) {
        toast.error(e?.message ?? "Could not connect Google account");
      } finally {
        searchParams.delete("code");
        searchParams.delete("scope");
        setSearchParams(searchParams, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildPayload = () => {
    if (mode === "voice") return transcript.trim();
    if (mode === "text") return typedText.trim();
    if (mode === "sheet" && sheet?.rows?.length) {
      return `Spreadsheet file: ${sheet.fileName}\nColumns: ${(sheet.columns ?? []).join(", ")}\nRows (JSON):\n${JSON.stringify(sheet.rows.slice(0, 150))}`;
    }
    return "";
  };

  const handleAnalyse = async () => {
    const content = buildPayload();
    if (mode === "image" ? !image : !content) {
      toast.error("Add some input first");
      return;
    }

    setAnalysing(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Please log in to analyse data");

      const body = {
        systemPrompt: STRUCTURING_PROMPT,
        prompt:
          mode === "image"
            ? "Read this image and extract every piece of business data visible in it (numbers, product or service names, dates, amounts, quantities). Return a JSON array of objects."
            : content,
        images: mode === "image" && image ? [{ data: image.base64, mimeType: image.mimeType }] : undefined,
        responseMimeType: "application/json",
        temperature: 0.2,
        maxOutputTokens: 4096,
      };

      let data: any = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        const res = await supabase.functions.invoke("gemini-ai", { body });
        if (!res.error) { data = res.data; break; }
        const { message, status } = await readFunctionError(res.error);
        const retryable = status === 429 || (status ?? 0) >= 500;
        if (!retryable || attempt === 1) throw new Error(message);
        await new Promise((r) => setTimeout(r, 2500));
      }

      if (data?.error) throw new Error(data.error);

      const text: string = data?.text ?? data?.response ?? "";
      let parsed: any;
      try {
        const match = text.match(/```json\s*([\s\S]*?)```/) || text.match(/(\[[\s\S]*\]|\{[\s\S]*\})/);
        parsed = JSON.parse(match ? (match[1] || match[0]) : text);
      } catch {
        throw new Error("The AI response could not be read as structured data. Try rephrasing your input.");
      }

      const list: any[] = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.rows)
          ? parsed.rows
          : Array.isArray(parsed?.data)
            ? parsed.data
            : [];

      if (!list.length) throw new Error("No structured rows could be extracted from that input.");

      setRows(list.map((r) => (typeof r === "object" && r !== null ? r : { Value: r })));
      toast.success(`Structured ${list.length} rows`);
    } catch (e: any) {
      setError(e?.message ?? "Analysis failed");
      toast.error(e?.message ?? "Analysis failed");
    } finally {
      setAnalysing(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!rows.length) return;
    downloadXlsx(`smart-data-entry-${new Date().toISOString().slice(0, 10)}.xlsx`, rows);
    toast.success("Excel file downloaded");
  };

  const connectGoogle = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const { data, error: fnError } = await supabase.functions.invoke("google-sheets-auth", {
      body: { action: "get_credentials" },
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });
    const clientId = data?.credentials?.clientId;
    if (fnError || !clientId) {
      throw new Error(
        "Google Sheets export isn't switched on yet. Use 'Download Excel' instead — it opens in Google Sheets too.",
      );
    }
    sessionStorage.setItem("sde_google_oauth", "1");
    const redirectUri = `${window.location.origin}/smart-data-entry`;
    window.location.href =
      `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code` +
      `&scope=${encodeURIComponent(GOOGLE_SCOPES)}&access_type=offline&prompt=consent`;
  };

  const handleExportSheets = async () => {
    if (!rows.length) return;
    setExporting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Please log in first");

      const values = [columns, ...rows.map((r) => columns.map((c) => (r[c] ?? "")))];
      const { data, error: fnError } = await supabase.functions.invoke("google-sheets-auth", {
        body: {
          action: "create_sheet",
          title: `Smart Data Entry — ${new Date().toLocaleString()}`,
          values,
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (data?.needsAuth || fnError?.message?.includes("401")) {
        toast.info("Connect your Google account to continue");
        await connectGoogle();
        return;
      }
      if (fnError) {
        const { message } = await readFunctionError(fnError);
        if (/credentials not configured/i.test(message)) {
          throw new Error("Google Sheets export isn't switched on yet. Use 'Download Excel' instead — it opens in Google Sheets too.");
        }
        throw new Error(message);
      }
      if (data?.error) throw new Error(data.error);

      window.open(data.url, "_blank", "noopener");
      toast.success("Google Sheet created");
    } catch (e: any) {
      toast.error(e?.message ?? "Google Sheets export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleSaveToDashboard = async () => {
    if (!rows.length) return;
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Please log in first");

      const { data: run, error: runError } = await supabase
        .from("workflow_runs")
        .insert({
          user_id: user.id,
          version: 1,
          status: "completed",
          input_source: `smart_data_entry:${mode}`,
          records_processed: rows.length,
          completed_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (runError) throw runError;

      const { error: dataError } = await supabase.from("workflow_data").insert(
        rows.map((row, index) => ({
          workflow_run_id: run.id,
          data_type: "cleaned",
          row_data: row,
          row_index: index,
        })),
      );
      if (dataError) throw dataError;

      const { error: importError } = await supabase.from("data_imports").insert({
        user_id: user.id,
        file_name: sheet?.fileName ?? image?.name ?? `Smart Data Entry (${mode})`,
        file_type: mode,
        import_status: "completed",
        records_processed: rows.length,
        metadata: { source: "smart_data_entry", mode, columns },
        completed_at: new Date().toISOString(),
      });
      if (importError) throw importError;

      toast.success(`Saved ${rows.length} rows to your dashboard`);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save to dashboard");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <SEO
        title="Smart Data Entry — Veylo AI"
        description="Capture business data by voice, text, spreadsheet or photo and let AI structure it into clean rows for any industry."
        path="/smart-data-entry"
      />

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" /> Smart Data Entry
          </h1>
          <p className="text-muted-foreground mt-1">
            Speak, type, upload a spreadsheet or snap a photo — AI detects your industry and structures it into clean rows.
          </p>
        </div>

        <Card className="p-6">
          <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              <TabsTrigger value="voice" className="gap-2"><Mic className="h-4 w-4" /> Voice</TabsTrigger>
              <TabsTrigger value="text" className="gap-2"><Type className="h-4 w-4" /> Text</TabsTrigger>
              <TabsTrigger value="sheet" className="gap-2"><FileSpreadsheet className="h-4 w-4" /> CSV / Excel</TabsTrigger>
              <TabsTrigger value="image" className="gap-2"><ImageIcon className="h-4 w-4" /> Image</TabsTrigger>
            </TabsList>

            <TabsContent value="voice" className="mt-6">
              <VoiceCapture value={transcript} onChange={setTranscript} />
            </TabsContent>

            <TabsContent value="text" className="mt-6">
              <Textarea
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                rows={10}
                placeholder="Sold 15 units of red shoes at $30 each on Monday. Had 2 returns."
                className="text-base"
              />
            </TabsContent>

            <TabsContent value="sheet" className="mt-6">
              <SheetCapture parsed={sheet} onChange={setSheet} />
            </TabsContent>

            <TabsContent value="image" className="mt-6">
              <ImageCapture image={image} onChange={setImage} />
            </TabsContent>
          </Tabs>

          <div className="mt-6 flex justify-end">
            <Button onClick={handleAnalyse} disabled={analysing} className="gap-2">
              {analysing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {analysing ? "Analysing…" : "Analyse and Structure"}
            </Button>
          </div>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </Card>

        {rows.length > 0 && (
          <Card className="p-6 space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Structured preview</h2>
              <p className="text-sm text-muted-foreground">
                {rows.length} rows · {columns.length} columns · edit any cell before exporting
              </p>
            </div>

            <StructuredPreviewTable columns={columns} rows={rows} onChange={setRows} />

            <div className="flex flex-wrap gap-3">
              <Button onClick={handleDownloadExcel} className="gap-2">
                <Download className="h-4 w-4" /> Download as Excel
              </Button>
              <Button variant="outline" onClick={handleExportSheets} disabled={exporting} className="gap-2">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                Export to Google Sheets
              </Button>
              <Button variant="outline" onClick={handleSaveToDashboard} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                Save to Dashboard
              </Button>
            </div>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default SmartDataEntry;
